import { getVideoContentTypeLabel, formatFileSize } from "../../../../components/videoSection/videoDetails";
import {IVideo, formatVideoName} from "../hooks/useFetchVideos";
import axiosInstant from "../../../../api";
import {zipFiles} from "../../../../api/endpoints";

interface IOptions {
    index?: number;
    type: string;
    extension: string;
}

export interface IFile {
    type: string;
    name: string;
    url: any;
    key: string;
    /** Bytes, when the discovery probe was able to read it. See `sizeCache`. */
    size?: number | null;
}

const existsCache = new Map<string, boolean>();

/**
 * Byte sizes learned as a SIDE EFFECT of `exists()` — no extra request is ever
 * made for them. `Content-Length` is a CORS-safelisted response header, so the
 * HEAD probe can read it cross-origin. The ranged-GET fallback must instead
 * parse the total out of `Content-Range` ("bytes 0-0/12345"), which is NOT
 * safelisted — it is readable only when the origin exposes it. We try, and fall
 * back to null. Never record the 1-byte `Content-Length` of a 206: callers
 * would print "1 B".
 */
const sizeCache = new Map<string, number | null>();

export const getKnownSize = (url: string): number | null =>
    sizeCache.get(url) ?? null;

// Lives in videoDetails (the card and details header use it too); re-exported
// so existing imports keep working.
export { formatFileSize };

export const formatName = (videoObject: IVideo) => {
    if (videoObject.name) return videoObject.name;
    return formatVideoName(String(videoObject.key || videoObject.title || ""));
};

export const formatDurationLabel = (length?: number | string | null): string => {
    const total = Math.round(Number(length));
    if (!Number.isFinite(total) || total <= 0) return "";
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    if (!minutes) return `${seconds}s`;
    return seconds ? `${minutes}m${seconds}s` : `${minutes}m`;
};

const sanitizeNamePart = (value: any): string =>
    String(value ?? "")
        .replace(/:/g, "x") // "4:5" -> "4x5", the way s3_files spells ratios
        .replace(/[/\\<>"|?*\x00-\x1f]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");

// Values that say nothing on their own ("With" what?) get their option name
// prefixed; a specific value ("Spanish", "Brand Logo") stands alone.
const GENERIC_OPTION_VALUES = new Set([
    "with",
    "without",
    "none",
    "default",
    "any",
    "minimum",
    "maximum",
]);

/**
 * The project's advanced-option values this file was cut for, but only the
 * ones that differ from the option's default: a default-only file ("English",
 * "TDC Watermark") adds nothing to its name. Grouped per option in option-id
 * order so the label is stable across downloads.
 */
export const buildOptionAttributes = (video: any): string[] => {
    const links: any[] = Array.isArray(video?.option_selection_links)
        ? video.option_selection_links
        : [];
    const byOption = new Map<number, { name: string; type?: string; values: string[] }>();
    links.forEach((link) => {
        const selection = link?.selection;
        const value = selection?.option_value;
        const option = value?.option;
        if (!option?.id || !option?.name) return;
        // `default` only arrives from an API that exposes it; without it we
        // cannot tell a default from an override, so we add nothing.
        if (value?.default !== false) return;
        let label = "";
        if (option.type === "value") {
            const n = selection?.numeric_value;
            label = n === null || n === undefined || n === "" ? "" : String(n);
        } else {
            label = String(value?.name || "");
        }
        if (!label) return;
        if (GENERIC_OPTION_VALUES.has(label.trim().toLowerCase())) {
            label = `${option.name} ${label}`;
        }
        const group = byOption.get(Number(option.id)) || {
            name: option.name,
            type: option.type,
            values: [] as string[],
        };
        if (!group.values.includes(label)) group.values.push(label);
        byOption.set(Number(option.id), group);
    });
    return [...byOption.entries()]
        .sort((a, b) => a[0] - b[0])
        .flatMap(([, group]) => group.values.map((v) => sanitizeNamePart(v)))
        .filter(Boolean);
};

export const buildDownloadAttributes = (video: any): string[] => {
    // Same spelling as the card's content-type chip: "how_to" -> "How To".
    const contentType = getVideoContentTypeLabel(video);
    return [
        sanitizeNamePart(contentType),
        sanitizeNamePart(video?.aspect_ratio),
        formatDurationLabel(video?.length),
        ...buildOptionAttributes(video),
    ].filter(Boolean);
};

const compactBasename = (basename: string, displayName: string): string => {
    const uuid = basename.match(
        /_([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(_[a-z_]+)?(\.[a-z0-9]+)$/i,
    );
    if (uuid) return `${uuid[1]}${uuid[2] || ""}${uuid[3]}`;

    const slug = displayName.trim().toLowerCase().replace(/\s+/g, "-");
    if (slug && basename.toLowerCase().startsWith(slug)) {
        const rest = basename.slice(slug.length).replace(/^[-_]+/, "");
        if (rest) return rest;
    }
    return basename;
};

export const buildDownloadFileName = (
    keyOrUrl: string,
    displayName?: string | null,
    video?: any,
): string => {
    // basename: drop any query string, take the last path segment
    const noQuery = String(keyOrUrl || "").split("?")[0];
    const basename = noQuery.split("/").pop() || "download";

    const parts = [
        sanitizeNamePart(displayName).slice(0, 120),
        ...(video ? buildDownloadAttributes(video) : []),
    ].filter(Boolean);

    if (!parts.length) return basename;
    return `${parts.join("_")}_${compactBasename(basename, String(displayName || ""))}`;
};

export const formatWatermarked = (video: IVideo) => {
    let url = video.location || video.reviewS3File?.location || "";
    let key = video.key;
    let ext = "." + url?.substr(url.lastIndexOf(".") + 1);
    url = url?.replace(ext, "_watermark" + ext);
    key = key?.replace(ext, "_watermark" + ext);
    return {url: url, key: key};
};

export const formatWebM = (video?: IVideo) => {
    if (!video) return {url: "", key: ""};
    let url = video.location || video.reviewS3File?.location || "";
    let key = video.key;
    let ext = "." + url?.substr(url.lastIndexOf(".") + 1);
    url = url?.replace(ext, "_optimized.webm");
    key = key?.replace(ext, "_optimized.webm");
    return {url: url, key: key};
};

export const formatWebOptimized = (video: IVideo) => {
    let url = video.location || video.reviewS3File?.location || "";
    let key = video.key;
    let ext = "." + url?.substr(url.lastIndexOf(".") + 1);
    url = url?.replace(ext, "_optimized" + ext);
    key = key?.replace(ext, "_optimized" + ext);
    return {url: url, key: key};
};

export const formatHighResolution = (video: IVideo) => {
    let url = video.location || video.reviewS3File?.location || "";
    let key = video.key;
    let ext = "." + url?.substr(url.lastIndexOf(".") + 1);
    url = url?.replace(ext, "_original" + ext);
    key = key?.replace(ext, "_original" + ext);
    return {url: url, key: key};
};

const formatCC = (
    video: IVideo,
    ext: string,
    dir: "cc" | "transcripts" = "cc",
) => {
    let url = video.location || video.reviewS3File?.location || "";
    let key = video.key || "";

    const process = (input: string, ext: string, dir: "cc" | "transcripts") => {
        if (!input) return "";
        // trim to before last "_..."
        let out = input.substr(0, input.lastIndexOf("_"));
        // insert directory after /tdc if not already /cc or /transcripts
        out = out.replace(/\/tdc(?!\/(cc|transcripts))/, `/tdc/${dir}`);
        const [first, ...props] = out.split("_");
        const rest = props.join("_");
        return `${first}-transcript_${rest}.${ext}`;
    };

    return {
        url: process(url, ext, dir),
        key: process(key, ext, dir),
    };
};

// Auto-transcribed path: `{hash}_v{version}_transcript.{ext}` alongside the video file
export const formatCCAutoTranscript = (video: IVideo, ext: "srt" | "vtt") => {
    const hash = video.hash;
    const version = Number(video.version) || 1;
    if (!hash) return {url: "", key: ""};

    const build = (path: string) => {
        if (!path) return "";
        const qIdx = path.indexOf("?");
        const clean = qIdx >= 0 ? path.slice(0, qIdx) : path;
        const lastSlash = clean.lastIndexOf("/");
        if (lastSlash === -1) return "";
        const dir = clean.slice(0, lastSlash);
        return `${dir}/${hash}_v${version}_transcript.${ext}`;
    };

    return {
        url: build(video.location || video.reviewS3File?.location || ""),
        key: build(video.key || ""),
    };
};

// New transcripts path
const formatCCNew = (video: IVideo, ext: string) => {
    const sourceUrl = video.location || video.reviewS3File?.location || "";
    const sourceKey = video.key || "";

    const build = (path: string) => {
        if (!path) return "";
        const qIdx = path.indexOf("?");
        const clean = qIdx >= 0 ? path.slice(0, qIdx) : path;

        const lastSlash = clean.lastIndexOf("/");
        if (lastSlash === -1) return "";

        const dir = clean.slice(0, lastSlash);
        const file = clean.slice(lastSlash + 1);

        // remove extension
        const dot = file.lastIndexOf(".");
        const nameNoExt = dot >= 0 ? file.slice(0, dot) : file;

        // remove the last underscore segment (trim like formatCC)
        const usIdx = nameNoExt.lastIndexOf("_");
        const base = usIdx >= 0 ? nameNoExt.slice(0, usIdx) : nameNoExt;
               
        // insert "-transcripts" before the FIRST underscore in the filename portion
        const firstUsIdx = base.indexOf("_");
        const tagged =
            firstUsIdx >= 0
                ? `${base.slice(0, firstUsIdx)}-transcript${base.slice(firstUsIdx)}`
                : `${base}-transcript`;

        return `${dir}/transcripts/${tagged}.${ext}`;
    };

    return {
        url: build(sourceUrl),
        key: build(sourceKey),
    };
};

/**
 * Resolve and fetch the actual SRT or VTT file for a video, probing all known
 * locations in priority order (auto-transcribed alongside the file → new-way
 * `/transcripts/` subfolder → legacy `/tdc/cc/` → legacy `/tdc/transcripts/`).
 * Returns the matched file's URL, S3 key, and content. Cache-busted so callers
 * see fresh content after an upload.
 */
export const resolveAndFetchTranscriptFile = async (
    video: IVideo,
    ext: "srt" | "vtt"
): Promise<{url: string; key: string; content: string} | null> => {
    const sourceUrl = video.location || video.reviewS3File?.location || "";
    const useNewWay = sourceUrl.includes("projects");

    const candidates: Array<{url: string; key: string}> = [];

    const auto = formatCCAutoTranscript(video, ext);
    if (auto.url) candidates.push(auto);

    if (useNewWay) {
        const newWay = formatCCNew(video, ext);
        if (newWay.url) candidates.push(newWay);
    } else {
        const cc = formatCC(video, ext, "cc");
        if (cc.url) candidates.push(cc);
        const t = formatCC(video, ext, "transcripts");
        if (t.url) candidates.push(t);
    }

    for (const c of candidates) {
        try {
            const bustUrl = `${c.url}${c.url.includes("?") ? "&" : "?"}t=${Date.now()}`;
            const res = await fetch(bustUrl, {cache: "no-store"});
            if (res.ok) {
                const content = await res.text();
                return {url: c.url, key: c.key, content};
            }
        } catch {
            // try next candidate
        }
    }

    return null;
};

export const fetchTranscripts = async (video: IVideo) => {
    const url = video.location || video.reviewS3File?.location || "";
    const useNewWay = url.includes("projects");
    const exts = ["srt", "vtt", "docx", "txt"] as const;
    const autoExts = ["srt", "vtt"] as const;

    // Auto-transcribed files (srt/vtt only) colocated with the s3 file
    const autoChecks = autoExts.map((ext) => {
        const urlKey = formatCCAutoTranscript(video, ext);
        if (!urlKey.url) return Promise.resolve(null as IFile | null);
        return exists(urlKey.url).then((ok): IFile | null =>
            ok ? {type: ext, name: `Transcript File .${ext}`, url: urlKey.url, key: urlKey.key, size: getKnownSize(urlKey.url)} : null
        );
    });

    let legacyChecks: Promise<IFile | null>[];
    if (useNewWay) {
        // All extensions in parallel
        legacyChecks = exts.map((ext) => {
            const urlKey = formatCCNew(video, ext);
            return exists(urlKey.url).then((ok): IFile | null =>
                ok ? {type: ext, name: `Transcript File .${ext}`, url: urlKey.url, key: urlKey.key, size: getKnownSize(urlKey.url)} : null
            );
        });
    } else {
        // Legacy: for each ext try /cc then /transcripts - parallelize across extensions
        legacyChecks = exts.map(async (ext): Promise<IFile | null> => {
            let urlKey = formatCC(video, ext, "cc");
            let ok = await exists(urlKey.url);
            if (!ok) {
                urlKey = formatCC(video, ext, "transcripts");
                ok = await exists(urlKey.url);
            }
            return ok ? {type: ext, name: `Transcript File .${ext}`, url: urlKey.url, key: urlKey.key, size: getKnownSize(urlKey.url)} : null;
        });
    }

    const [autoResults, legacyResults] = await Promise.all([
        Promise.all(autoChecks),
        Promise.all(legacyChecks),
    ]);

    // Dedupe by extension; auto-transcribed wins over legacy for the same type
    const byType = new Map<string, IFile>();
    (legacyResults.filter(Boolean) as IFile[]).forEach((f) => byType.set(f.type, f));
    (autoResults.filter(Boolean) as IFile[]).forEach((f) => byType.set(f.type, f));
    return Array.from(byType.values());
};

export const imageFormats = ["jpg", "tif", "png"];

export const getAutoGenThumbnail = (url: string, ext: string): string =>
    url?.replace(
        `.${url?.substring(url.lastIndexOf(".") + 1)}`,
        `_thumbnail${ext}`,
    );

export const getPlayIconThumbnail = (url: string): string => {
    if (!url) return url;
    return url?.replace(
        `.${url?.substring(url.lastIndexOf(".") + 1)}`,
        `_thumbnail_play_icon.png`,
    );
};

const insertThumbnailsDir = (url: string): string => {
    if (!url) return "";
    const lastSlash = url.lastIndexOf("/");
    if (lastSlash === -1) return url;
    return `${url.slice(0, lastSlash)}/thumbnails${url.slice(lastSlash)}`;
};

export const getStaticThumbnail = (url: string, options: IOptions, injectDir: boolean = true): string => {
    const {type, index, extension} = options;
    let image = "";

    if (url.includes("projects")) {
        image = url.substring(0, url.lastIndexOf("_"));
        if (injectDir) {
            image = image.replace(/\/([^/]*projects\/.+\/)([^/]+)$/, `/$1${type}/$2`);
        }
        const [first, second, ...data] = image.split("_");
        const rest = data.join("_");
        if (index) image = `${first}_${second}-thumbnail-${index}_${rest}`;
    } else {
        image = url.substring(0, url.lastIndexOf("_"));
        if (injectDir) {
            image = image.replace("tdc", "tdc/thumbnails");
        }
        const [first, ...data] = image.split("_");
        const rest = data.join("_");
        if (index) image = `${first}-thumbnail-${index}_${rest}`;
    }
    return `${image}.${extension}`;
};

const formatAutoGenThumbnails = (url: string, extension: string) => {
    if (!url) return "";
    switch (extension) {
        case "png":
            return getAutoGenThumbnail(url, ".png");
        case "playicon":
            return getAutoGenThumbnail(url, "_play_icon.png");
        default:
            return url;
    }
};

const formatStaticThumbnails = (
    url: string,
    extension: string,
    index?: number,
    injectDir: boolean = true,
) => {
    if (!url) return "";
    return getStaticThumbnail(url, {type: "thumbnails", extension, index}, injectDir);
};

// Iconik drag-and-drop thumbnails: `{folder}/thumbnails/{hash}-v{version}-{i}.{ext}`
// where folder is the video's parent dir, i is 1..thumbnail_count, ext is the
// dropped image's extension (jpg/png/tiff).
export const formatIconikThumbnail = (
    video: IVideo,
    count: number,
    ext: string,
) => {
    const sourceUrl = video.location || video.reviewS3File?.location || "";
    const sourceKey = video.key || "";
    const hash = video.hash;
    const version = Number(video.version) || 1;
    if (!hash) return { url: "", key: "" };

    const build = (path: string) => {
        if (!path) return "";
        const qIdx = path.indexOf("?");
        const clean = qIdx >= 0 ? path.slice(0, qIdx) : path;
        const lastSlash = clean.lastIndexOf("/");
        if (lastSlash === -1) return "";
        const dir = clean.slice(0, lastSlash);
        return `${dir}/thumbnails/${hash}-v${version}-${count}.${ext}`;
    };

    return { url: build(sourceUrl), key: build(sourceKey) };
};

export const fetchThumbnails = async (video: IVideo) => {
    const thumbnails = [] as Array<IFile>;
    const url = video.location || video.reviewS3File?.location;
    const key = video.key;

    // Auto-gen thumbnails: all 4 checks (2 variants × 2 paths) in parallel
    const autoGenChecks = (["png", "playicon"] as const).flatMap((variant) => {
        const name =
            variant === "png"
                ? "Auto Generated .png Thumbnail"
                : "Auto Generated .png Play Icon Thumbnail";
        const originalUrl = formatAutoGenThumbnails(String(url), variant);
        const originalKey = formatAutoGenThumbnails(String(key), variant);
        const withDirUrl = insertThumbnailsDir(originalUrl);
        const withDirKey = insertThumbnailsDir(originalKey);
        return [
            exists(originalUrl).then((found) =>
                found ? {type: variant, name, url: originalUrl, key: String(originalKey)} : null
            ),
            exists(withDirUrl).then((found) =>
                found ? {type: variant, name, url: withDirUrl, key: String(withDirKey)} : null
            ),
        ];
    });

    // Unnumbered static thumbnails: all 6 checks (3 formats × 2 paths) in parallel
    const unnumberedChecks = imageFormats.flatMap((format) =>
        [false, true].map((injectDir) => {
            const formattedUrl = formatStaticThumbnails(String(url), format, undefined, injectDir);
            const formattedKey = formatStaticThumbnails(String(key), format, undefined, injectDir);
            return exists(formattedUrl).then((found) =>
                found ? {type: format, name: `Thumbnail 1 ${format}`, url: formattedUrl, key: String(formattedKey)} : null
            );
        })
    );

    const [autoGenResults, unnumberedResults] = await Promise.all([
        Promise.all(autoGenChecks),
        Promise.all(unnumberedChecks),
    ]);
    autoGenResults.forEach((r) => r && thumbnails.push(r));
    unnumberedResults.forEach((r) => r && thumbnails.push(r));

    // Numbered static thumbnails: sequential per index, but all formats+paths at each index in parallel
    let i = 1;
    while (true) {
        const indexChecks = imageFormats.flatMap((format) =>
            [false, true].map((injectDir) => {
                const formattedUrl = formatStaticThumbnails(String(url), format, i, injectDir);
                const formattedKey = formatStaticThumbnails(String(key), format, i, injectDir);
                return exists(formattedUrl).then((found) =>
                    found ? {type: format, name: `Thumbnail ${i} .${format}`, url: formattedUrl, key: String(formattedKey)} : null
                );
            })
        );
        const indexResults = await Promise.all(indexChecks);
        const found = indexResults.filter(Boolean) as IFile[];
        if (found.length === 0) break;
        found.forEach((r) => thumbnails.push(r));
        i++;
    }

    // Iconik drag-and-drop thumbnails: hash-v{version}-{i}.{ext}, count tracked on s3_files.thumbnail_count
    const iconikCount = video.thumbnail_count || 0;
    if (iconikCount > 0 && video.hash) {
        const iconikChecks = Array.from({ length: iconikCount }, (_, idx) => idx + 1).flatMap((idx) =>
            imageFormats.map((ext) => {
                const formatted = formatIconikThumbnail(video, idx, ext);
                if (!formatted.url) return Promise.resolve(null as IFile | null);
                return exists(formatted.url).then((found): IFile | null =>
                    found
                        ? {
                              type: ext,
                              name: `Thumbnail ${idx} .${ext}`,
                              url: formatted.url,
                              key: formatted.key,
                          }
                        : null,
                );
            }),
        );
        const iconikResults = await Promise.all(iconikChecks);
        iconikResults.forEach((r) => r && thumbnails.push(r));
    }

    return thumbnails;
};

export const exists = async (customUrl: string, cacheBust: boolean = false) => {
    if (!cacheBust && existsCache.has(customUrl)) {
        return existsCache.get(customUrl)!;
    }

    let url = customUrl;
    if (cacheBust)
        url = `${url}${customUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    // NB: these deliberately do NOT swallow a rejection. A 404 (res.ok === false)
    // means the derivative was never generated; a REJECTION means the network, DNS,
    // CORS or the bucket is down, and reporting that as "absent" is what made the
    // Thumbnails and Transcripts panels render "None were generated or uploaded"
    // during an outage — with their error states and Retry unreachable dead code.
    const tryHead = async () => {
        const res = await fetch(url, {
            method: "HEAD",
            signal: controller.signal,
            cache: "no-store",
        });
        if (res.ok) {
            const len = Number(res.headers.get("content-length"));
            sizeCache.set(customUrl, Number.isFinite(len) && len > 0 ? len : null);
        }
        return res.ok;
    };

    const tryGetRange = async () => {
        const res = await fetch(url, {
            method: "GET",
            headers: {Range: "bytes=0-0"},
            signal: controller.signal,
            cache: "no-store",
        });
        if (res.ok) {
            // "bytes 0-0/12345" -> 12345. Absent or "*" means unknown.
            const total = (res.headers.get("content-range") || "").split("/")[1];
            const parsed = Number(total);
            sizeCache.set(
                customUrl,
                total && total !== "*" && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
            );
        }
        return res.ok;
    };

    // HEAD is refused outright by some bucket policies, so a HEAD rejection alone
    // proves nothing — the range probe is the arbiter. Only when BOTH fail at the
    // network layer does this throw, and nothing is cached, so a retry re-probes
    // rather than replaying a memoised false for the life of the page.
    let ok = false;
    try {
        ok = await tryHead();
    } catch {
        ok = false;
    }
    if (!ok) {
        try {
            ok = await tryGetRange();
        } catch (err) {
            window.clearTimeout(timeoutId);
            throw err;
        }
    }

    window.clearTimeout(timeoutId);
    existsCache.set(customUrl, ok);
    return ok;
};

export enum DownloadType {
    ALL = "all",
    HIGH_RES = "high_resolution",
    WEB = "web_optimized",
    WEBM = "webm",
    THUMB = "thumbnail",
    TRANSCRIPT = "transcript",
    WATERMARK = "watermark",
}

interface FileTypeMeta {
    title: string;
    description: string;
}

export const FILE_TYPE_META: Record<DownloadType, FileTypeMeta> = {
    [DownloadType.ALL]: {
        title: "All content",
        description: "Every format in one .zip",
    },
    [DownloadType.HIGH_RES]: {
        title: "HD Original",
        description: "Original, full-resolution video file",
    },
    [DownloadType.WEB]: {
        title: "HD Web Optimized",
        description: "Compressed mp4 for web & streaming",
    },
    [DownloadType.WEBM]: {
        title: "HD WebM",
        description: "Optimized WebM for web & embeds",
    },
    [DownloadType.THUMB]: {
        title: "Thumbnails",
        description: "Still image thumbnails (.zip)",
    },
    [DownloadType.TRANSCRIPT]: {
        title: "Transcripts/CC",
        description: "Caption & transcript files (SRT / VTT)",
    },
    [DownloadType.WATERMARK]: {
        title: "Watermarked",
        description: "Same video with a visible watermark",
    },
};

export interface IBtnText {
    [key: string]: {
        title: string;
        text?: string;
    };
}

export const BtnText: IBtnText = Object.fromEntries(
    (Object.keys(FILE_TYPE_META) as DownloadType[]).map((key) => [
        key,
        {
            title: FILE_TYPE_META[key].title,
            text: FILE_TYPE_META[key].description,
        },
    ]),
);

export interface FileTypeConfigOption {
    key: DownloadType;
    title: string;
    description: string;
    always: boolean;
}

/** File types brands cannot disable. Watermarked is the only variant served
 *  for unapproved videos; only HD Web Optimized and HD WebM are toggleable. */
export const ALWAYS_ON_FILE_TYPES: DownloadType[] = [
    DownloadType.HIGH_RES,
    DownloadType.WATERMARK,
    DownloadType.THUMB,
    DownloadType.TRANSCRIPT,
];

export const BRAND_FILE_TYPE_CONFIG: FileTypeConfigOption[] = (
    Object.keys(FILE_TYPE_META) as DownloadType[]
)
    .filter((key) => key !== DownloadType.ALL)
    .map((key) => ({
        key,
        title: FILE_TYPE_META[key].title,
        description: FILE_TYPE_META[key].description,
        always: ALWAYS_ON_FILE_TYPES.includes(key),
    }));

export const getBrandAllowedFileTypes = (
    brands:
        | Array<{enabled_file_types?: string[]; rmn?: boolean} | null | undefined>
        | null
        | undefined,
): Set<string> | null => {
    const list = (brands || []).filter(Boolean) as Array<{
        enabled_file_types?: string[];
        rmn?: boolean;
    }>;
    const rmn = list.find(
        (b) => b?.rmn === true && b?.enabled_file_types?.length,
    );
    if (rmn) return new Set(rmn.enabled_file_types);

    const configured = list.filter((b) => b?.enabled_file_types?.length);
    if (configured.length === 0) return null;

    return new Set(configured[0].enabled_file_types);
};

export const isDownloadTypeVisible = (
    type: DownloadType,
    allowed: Set<string> | null,
): boolean => {
    if (type === DownloadType.ALL || ALWAYS_ON_FILE_TYPES.includes(type))
        return true;
    if (!allowed) return true;
    return allowed.has(type);
};

export const formatTime = (time: number): string => {
    if (!time) return "";

    let output = "";

    const hours = Math.floor(time / 3600);
    output += hours ? `${hours} hr ` : "";

    const minutes = Math.floor((time % 3600) / 60);
    output += minutes ? `${minutes} mins ` : "";

    const seconds = Math.floor(time % 60);
    return `${output} ${seconds} secs`;
};

export interface ZipFileEntry {
    key: string;
    name: string;
    // Zip folder replacing the key's `<channel>_channel_<id>` segment. That
    // segment is per content channel, so a project's channel instances need
    // this to land in separate folders.
    folder?: string;
}

export interface ProcessDownloadResult {
    source?: string;
    fileName?: string;
    bucket?: string;
    key?: string;
    keys?: string[];
    files?: ZipFileEntry[];
    error?: string;
}

export const processSingleVideoDownload = async (
    video: IVideo,
    type: DownloadType,
    zipFileName: string,
    returnKeysOnly: boolean = false,
    onlyWatermark: boolean = false,
    allowedTypes: Set<string> | null = null,
): Promise<ProcessDownloadResult> => {
    const payload: {zipFileName: string; files: ZipFileEntry[]} = {
        zipFileName,
        files: [],
    };

    // Build a zip entry that carries the human-readable display name alongside the key.
    const entry = (key: string): ZipFileEntry => ({
        key,
        name: buildDownloadFileName(key, formatName(video), video),
    });

    // Result for a single direct (non-zip) download: the source URL plus the
    // metadata DownloadBtn needs to presign the file with a forced filename.
    const single = (url: string, key: string): ProcessDownloadResult => ({
        source: url,
        fileName: buildDownloadFileName(key, formatName(video), video),
        bucket: video.bucket,
        key,
    });

    if (onlyWatermark) {
        const watermarkVideo = formatWatermarked(video);
        if (returnKeysOnly)
            return watermarkVideo?.key
                ? {keys: [watermarkVideo.key], files: [entry(watermarkVideo.key)]}
                : {keys: [], files: []};
        return single(watermarkVideo.url, watermarkVideo.key);
    }

    switch (type) {
        case DownloadType.WATERMARK: {
            const watermarkVideo = formatWatermarked(video);
            if (returnKeysOnly)
                return watermarkVideo?.key
                    ? {keys: [watermarkVideo.key], files: [entry(watermarkVideo.key)]}
                    : {keys: [], files: []};
            return single(watermarkVideo.url, watermarkVideo.key);
        }

        case DownloadType.HIGH_RES: {
            const highResVideo = formatHighResolution(video);
            if (returnKeysOnly)
                return highResVideo?.key
                    ? {keys: [highResVideo.key], files: [entry(highResVideo.key)]}
                    : {keys: [], files: []};
            return single(highResVideo.url, highResVideo.key);
        }

        case DownloadType.WEB: {
            const webOptVideo = formatWebOptimized(video);
            if (returnKeysOnly)
                return webOptVideo?.key
                    ? {keys: [webOptVideo.key], files: [entry(webOptVideo.key)]}
                    : {keys: [], files: []};
            return single(webOptVideo.url, webOptVideo.key);
        }

        case DownloadType.WEBM: {
            const webMVideo = formatWebM(video);
            if (returnKeysOnly)
                return webMVideo?.key
                    ? {keys: [webMVideo.key], files: [entry(webMVideo.key)]}
                    : {keys: [], files: []};
            return single(webMVideo.url, webMVideo.key);
        }

        case DownloadType.THUMB: {
            const thumbnails = await fetchThumbnails(video).catch(() => [] as IFile[]);
            if (returnKeysOnly)
                return {
                    keys: thumbnails.map((t) => t.key),
                    files: thumbnails.map((t) => entry(t.key)),
                };
            thumbnails.forEach((t) => payload.files.push(entry(t.key)));
            const thumbZip = await axiosInstant.post(zipFiles(), payload);
            return {source: thumbZip.data.fileName};
        }

        case DownloadType.TRANSCRIPT: {
            const transcripts = await fetchTranscripts(video).catch(() => [] as IFile[]);
            if (!transcripts || transcripts.length === 0)
                return {error: "No transcripts available for this video."};
            if (returnKeysOnly)
                return {
                    keys: transcripts.map((t) => t.key),
                    files: transcripts.map((t) => entry(t.key)),
                };
            transcripts.forEach((t) => payload.files.push(entry(t.key)));
            const transcriptZip = await axiosInstant.post(zipFiles(), payload);
            return {source: transcriptZip.data.fileName};
        }

        case DownloadType.ALL: {
            const allow = (t: DownloadType) => !allowedTypes || allowedTypes.has(t);

            const [thumbnails, transcripts] = await Promise.all([
                fetchThumbnails(video).catch(() => [] as IFile[]),
                fetchTranscripts(video).catch(() => [] as IFile[]),
            ]);

            const keys: string[] = [formatHighResolution(video).key];
            if (allow(DownloadType.WEB)) keys.push(formatWebOptimized(video).key);
            if (allow(DownloadType.WEBM)) keys.push(formatWebM(video).key);
            keys.push(formatWatermarked(video).key);
            thumbnails.forEach((t) => keys.push(t.key));
            transcripts.forEach((t) => keys.push(t.key));

            if (returnKeysOnly) {
                return {keys, files: keys.map((k) => entry(k))};
            }

            keys.forEach((k) => payload.files.push(entry(k)));

            const allZip = await axiosInstant.post(zipFiles(), payload);
            return {source: allZip.data.fileName};
        }

        default:
            return {error: "Unknown download type."};
    }
};